import type { MeasurementEntry } from '@fasting-timer/shared'

import { getLocale, translate } from '../../i18n'
import { getEntryTimestampMs } from '../../state/measurementsStore'
import { getSleepEndMs } from '../../state/sleepStore'
import type { VolumeBucket } from '../measurements/metricViewUtils'

/**
 * Formats a sleep length as hours and minutes (e.g. `7h 32m`).
 *
 * @param seconds - The length in seconds.
 * @returns The formatted length.
 */
export function formatSleepLength(seconds: number) {
   const totalMinutes = Math.max(0, Math.round(seconds / 60))
   return translate('{hours}h {minutes}m', {
      hours: Math.floor(totalMinutes / 60),
      minutes: String(totalMinutes % 60).padStart(2, '0'),
   })
}

/**
 * Formats a time of day in the active locale (e.g. `06:42`).
 *
 * @param timestamp - Epoch ms.
 * @returns The formatted time.
 */
export function formatClockTime(timestamp: number) {
   return new Date(timestamp).toLocaleTimeString(getLocale(), {
      hour: '2-digit',
      minute: '2-digit',
   })
}

/**
 * Describes when a sleep happened: `Oct 28, 23:10 – 06:42`, or only the start while running.
 *
 * @param entry - The sleep entry.
 * @returns The formatted span.
 */
export function formatSleepSpan(entry: MeasurementEntry) {
   const startMs = getEntryTimestampMs(entry)
   const start = new Date(startMs).toLocaleString(getLocale(), {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
   })
   const endMs = getSleepEndMs(entry)
   return endMs === null ? start : `${start} – ${formatClockTime(endMs)}`
}

/**
 * Moves each ended sleep to its wake-up time, so a night counts for the morning it ends.
 *
 * @param entries - Ended sleep entries.
 * @returns Copies timestamped at the wake-up time.
 */
export function atWakeUpTime(entries: readonly MeasurementEntry[]): MeasurementEntry[] {
   return entries.flatMap(entry => {
      const endMs = getSleepEndMs(entry)
      return endMs === null ?
            []
         :  [{ ...entry, timestamp: new Date(endMs).toISOString() }]
   })
}

/**
 * Turns summed buckets into average sleep per night, so weeks and months read in
 * the same hours as single days.
 *
 * @param buckets - Buckets holding summed durations.
 * @returns Buckets whose total is the mean of their parts.
 */
export function averageBuckets(buckets: readonly VolumeBucket[]): VolumeBucket[] {
   return buckets.map(bucket => ({
      ...bucket,
      total: bucket.parts.length > 0 ? bucket.total / bucket.parts.length : 0,
      parts: [],
   }))
}
